import { motion } from "framer-motion";
import { Blocks, Users, Wrench, Sparkles } from "lucide-react";
import { PageHeader, DISCORD_URL, fadeUp, cardReveal, staggerContainer } from "@/components/shared";
import CTA from "@/components/CTA";
import { SEO } from "@/components/shared";

const VALUES = [
  {
    icon: Blocks,
    title: "Crafted Code",
    text: "Every plugin is written with performance and stability in mind — no shortcuts, no bloat.",
    testid: "about-value-crafted-code",
  },
  {
    icon: Users,
    title: "Community First",
    text: "We build around people. Servers grow when the tools behind them actually work.",
    testid: "about-value-community-first",
  },
  {
    icon: Wrench,
    title: "Real Support",
    text: "Setup, configuration, troubleshooting — we stay with you long after the first launch.",
    testid: "about-value-real-support",
  },
  {
    icon: Sparkles,
    title: "Fresh Ideas",
    text: "From unique gameplay mechanics to custom integrations, we love solving new problems.",
    testid: "about-value-fresh-ideas",
  },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Us — Vexora Development"
        description="Vexora Development is a community focused on Minecraft development, custom plugins, and server solutions — helping communities grow around the work they do."
      />
      <PageHeader
        eyebrow="About Us"
        title={
          <>
            Built by developers,
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-500 bg-clip-text text-transparent">
              driven by community.
            </span>
          </>
        }
        subtitle="Vexora Development is a community focused on Minecraft development, custom plugins, and server solutions — helping communities grow around the work they do."
      />

      <section data-testid="about-story-section" className="relative pb-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeUp}
              className="lg:col-span-7 space-y-5"
            >
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Our story
              </h2>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                Vexora Development started with a simple goal: make it easy
                for any Minecraft community to run a server they can be proud
                of. What began as a small group of developers helping friends
                fix plugins has grown into a community of creators building
                custom features, optimizing servers, and sharing what we
                learn along the way.
              </p>
              <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
                Today we support servers and communities of every size — from
                a single plugin request to full server setups. Our focus is
                always the same: reliable, high-quality work that helps your
                community grow.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.21, 0.65, 0.35, 1] }}
              className="lg:col-span-5 relative"
            >
              <div className="absolute -inset-6 bg-purple-600/15 blur-3xl rounded-full pointer-events-none" />
              <div className="relative rounded-2xl border border-purple-500/20 overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                <img
                  src="https://images.pexels.com/photos/34697345/pexels-photo-34697345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Developer working at a neon-lit setup"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090a0f]/70 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        data-testid="about-values-section"
        className="relative py-24 lg:py-28 border-t border-purple-500/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-purple-400 mb-4">
              What Drives Us
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight">
              The values behind the work
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {VALUES.map((value) => (
              <motion.div
                key={value.title}
                variants={cardReveal}
                data-testid={value.testid}
                className="group h-full rounded-2xl border border-purple-500/15 bg-[#11131f]/80 p-7 transition-all duration-300 hover:border-purple-500/50 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(147,51,234,0.18)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-500/25 bg-purple-500/10 transition-colors duration-300 group-hover:bg-purple-500/20">
                  <value.icon className="w-5 h-5 text-purple-400" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-white">{value.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{value.text}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 max-w-2xl text-sm text-slate-500"
          >
            Want to be part of it?{" "}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="about-discord-inline-link"
              className="text-purple-400 hover:text-purple-300 underline decoration-purple-500/40 underline-offset-4 transition-colors"
            >
              Join the Discord community
            </a>{" "}
            and build with us.
          </motion.p>
        </div>
      </section>

      <CTA />
    </>
  );
}
